/**
 * Role → dashboard mapping and routing helpers.
 * Used by Login, VerifyOTP, and the role picker to avoid exposing all roles upfront.
 * The backend returns the user's roles after authentication; we only present those.
 */

export const ROLE_DASHBOARDS = [
  { apiRole: 'ADMIN', uiRole: 'admin', label: 'System Admin', route: '/system-admin', icon: '🛡️', description: 'Manage the entire platform' },
  { apiRole: 'LANDLORD', uiRole: 'landlord', label: 'Landlord', route: '/landlord', icon: '🏠', description: 'Manage properties & tenants' },
  { apiRole: 'BNB_HOST', uiRole: 'bnb-host', label: 'BnB Host', route: '/airbnb-host', icon: '🏨', description: 'Manage short-stay listings' },
  { apiRole: 'AGENT', uiRole: 'agent', label: 'Agent', route: '/agent', icon: '🤝', description: 'Manage client properties' },
  { apiRole: 'TENANT', uiRole: 'tenant', label: 'Tenant', route: '/listings', icon: '🔎', description: 'Browse & rent properties' },
  { apiRole: 'GUEST', uiRole: 'guest', label: 'Guest', route: '/listings', icon: '👤', description: 'Browse listings' },
];

const API_TO_UI = {
  ADMIN: 'admin',
  LANDLORD: 'landlord',
  BNB_HOST: 'bnb-host',
  AGENT: 'agent',
  TENANT: 'tenant',
  GUEST: 'guest',
};

/**
 * Get the dashboard entries available to a user based on their roles.
 * @param {string[]} apiRoles - Roles from backend (case-insensitive)
 * @returns {Array} Matching ROLE_DASHBOARDS entries (deduplicated by route)
 */
export const getAvailableDashboards = (apiRoles = []) => {
  const normalized = (Array.isArray(apiRoles) ? apiRoles : []).map((r) => String(r).toUpperCase());
  const seen = new Set();
  const result = [];
  for (const dash of ROLE_DASHBOARDS) {
    if (normalized.includes(dash.apiRole) && !seen.has(dash.route)) {
      seen.add(dash.route);
      result.push(dash);
    }
  }
  return result;
};

/**
 * Get the UI role string for a given API role.
 */
export const apiRoleToUiRole = (apiRole) => API_TO_UI[String(apiRole).toUpperCase()] || null;

/**
 * Resolve the final destination path for a chosen dashboard + optional `from` redirect.
 * @param {Object} dash - A ROLE_DASHBOARDS entry
 * @param {string|null} from - Intended path from a protected-route redirect
 * @returns {string}
 */
export const resolveDestination = (dash, from) => {
  if (from && typeof from === 'string') {
    // Property-page redirects should land on the tenant property view.
    if (from.startsWith('/property/')) {
      return from.replace('/property/', '/tenant/property/');
    }
    return from;
  }
  return dash.route;
};

/**
 * Determine if a `from` path implies a tenant/guest context (e.g. property detail page).
 */
export const fromImpliesTenant = (from) =>
  typeof from === 'string' && (from.includes('/property/') || from.includes('/tenant/') || from.includes('/listings'));
